import { normalizeLocale, type SupportedLocale } from '@/i18n/config';

export type GroundedWorkUiCopy = {
  assumptionsAndLimits: string;
  createdFor: string;
  evidenceAndMethods: string;
  generationDetails: string;
  hideFullDocument: string;
  linkedSectionMayHaveMoved: string;
  moreImages: string;
  openDocument: string;
  relatedOutputs: string;
  researchAsOf: string;
  showFullDocument: string;
  sources: string;
  statusComplete: string;
  statusInProgress: string;
  statusPartial: string;
  statusSuperseded: string;
  viewContextAndRationale: string;
  viewImage: string;
  viewRationale: string;
  viewResearchAndRationale: string;
};

const copy: Record<SupportedLocale, GroundedWorkUiCopy> = {
  en: {
    assumptionsAndLimits: 'Assumptions, limits, and validation',
    createdFor: 'Created for {title}',
    evidenceAndMethods: 'Evidence and methods',
    generationDetails: 'Generation details',
    hideFullDocument: 'Hide full document',
    linkedSectionMayHaveMoved: 'Linked section may have moved',
    moreImages: 'More images: {count}',
    openDocument: 'Open document',
    relatedOutputs: 'Related outputs',
    researchAsOf: 'Research current to {date}',
    showFullDocument: 'Show full document',
    sources: 'Sources',
    statusComplete: 'Complete',
    statusInProgress: 'In progress',
    statusPartial: 'Partial',
    statusSuperseded: 'Superseded',
    viewContextAndRationale: 'View context and rationale',
    viewImage: 'View image',
    viewRationale: 'View rationale',
    viewResearchAndRationale: 'View research and rationale',
  },
  ar: {
    assumptionsAndLimits: 'الافتراضات والقيود والتحقق',
    createdFor: 'أُنشئ من أجل {title}',
    evidenceAndMethods: 'الأدلة والأساليب',
    generationDetails: 'تفاصيل الإنشاء',
    hideFullDocument: 'إخفاء المستند الكامل',
    linkedSectionMayHaveMoved: 'ربما تم نقل القسم المرتبط',
    moreImages: 'صور إضافية: {count}',
    openDocument: 'فتح المستند',
    relatedOutputs: 'النواتج المرتبطة',
    researchAsOf: 'البحث محدّث حتى {date}',
    showFullDocument: 'عرض المستند الكامل',
    sources: 'المصادر',
    statusComplete: 'مكتمل',
    statusInProgress: 'قيد التنفيذ',
    statusPartial: 'جزئي',
    statusSuperseded: 'مستبدل',
    viewContextAndRationale: 'عرض السياق والمبررات',
    viewImage: 'عرض الصورة',
    viewRationale: 'عرض المبررات',
    viewResearchAndRationale: 'عرض البحث والمبررات',
  },
  de: {
    assumptionsAndLimits: 'Annahmen, Grenzen und Validierung',
    createdFor: 'Erstellt für {title}',
    evidenceAndMethods: 'Evidenz und Methoden',
    generationDetails: 'Generierungsdetails',
    hideFullDocument: 'Vollständiges Dokument ausblenden',
    linkedSectionMayHaveMoved:
      'Der verknüpfte Abschnitt wurde möglicherweise verschoben',
    moreImages: 'Weitere Bilder: {count}',
    openDocument: 'Dokument öffnen',
    relatedOutputs: 'Zugehörige Ergebnisse',
    researchAsOf: 'Recherche aktuell bis {date}',
    showFullDocument: 'Vollständiges Dokument anzeigen',
    sources: 'Quellen',
    statusComplete: 'Abgeschlossen',
    statusInProgress: 'In Bearbeitung',
    statusPartial: 'Teilweise',
    statusSuperseded: 'Ersetzt',
    viewContextAndRationale: 'Kontext und Begründung ansehen',
    viewImage: 'Bild ansehen',
    viewRationale: 'Begründung ansehen',
    viewResearchAndRationale: 'Recherche und Begründung ansehen',
  },
  es: {
    assumptionsAndLimits: 'Supuestos, límites y validación',
    createdFor: 'Creado para {title}',
    evidenceAndMethods: 'Evidencia y métodos',
    generationDetails: 'Detalles de generación',
    hideFullDocument: 'Ocultar documento completo',
    linkedSectionMayHaveMoved: 'Puede que la sección vinculada se haya movido',
    moreImages: 'Más imágenes: {count}',
    openDocument: 'Abrir documento',
    relatedOutputs: 'Resultados relacionados',
    researchAsOf: 'Investigación vigente hasta {date}',
    showFullDocument: 'Mostrar documento completo',
    sources: 'Fuentes',
    statusComplete: 'Completo',
    statusInProgress: 'En curso',
    statusPartial: 'Parcial',
    statusSuperseded: 'Reemplazado',
    viewContextAndRationale: 'Ver contexto y fundamento',
    viewImage: 'Ver imagen',
    viewRationale: 'Ver fundamento',
    viewResearchAndRationale: 'Ver investigación y fundamento',
  },
  fr: {
    assumptionsAndLimits: 'Hypothèses, limites et validation',
    createdFor: 'Créé pour {title}',
    evidenceAndMethods: 'Éléments probants et méthodes',
    generationDetails: 'Détails de génération',
    hideFullDocument: 'Masquer le document complet',
    linkedSectionMayHaveMoved: 'La section liée a peut-être été déplacée',
    moreImages: 'Images supplémentaires : {count}',
    openDocument: 'Ouvrir le document',
    relatedOutputs: 'Résultats associés',
    researchAsOf: 'Recherche à jour au {date}',
    showFullDocument: 'Afficher le document complet',
    sources: 'Sources',
    statusComplete: 'Terminé',
    statusInProgress: 'En cours',
    statusPartial: 'Partiel',
    statusSuperseded: 'Remplacé',
    viewContextAndRationale: 'Voir le contexte et le raisonnement',
    viewImage: 'Voir l’image',
    viewRationale: 'Voir le raisonnement',
    viewResearchAndRationale: 'Voir la recherche et le raisonnement',
  },
  hi: {
    assumptionsAndLimits: 'मान्यताएँ, सीमाएँ और सत्यापन',
    createdFor: '{title} के लिए बनाया गया',
    evidenceAndMethods: 'साक्ष्य और विधियाँ',
    generationDetails: 'जनरेशन विवरण',
    hideFullDocument: 'पूरा दस्तावेज़ छिपाएँ',
    linkedSectionMayHaveMoved:
      'लिंक किया गया अनुभाग शायद स्थानांतरित हो गया है',
    moreImages: 'और छवियाँ: {count}',
    openDocument: 'दस्तावेज़ खोलें',
    relatedOutputs: 'संबंधित आउटपुट',
    researchAsOf: '{date} तक शोध अद्यतन',
    showFullDocument: 'पूरा दस्तावेज़ दिखाएँ',
    sources: 'स्रोत',
    statusComplete: 'पूर्ण',
    statusInProgress: 'प्रगति में',
    statusPartial: 'आंशिक',
    statusSuperseded: 'प्रतिस्थापित',
    viewContextAndRationale: 'संदर्भ और तर्क देखें',
    viewImage: 'छवि देखें',
    viewRationale: 'तर्क देखें',
    viewResearchAndRationale: 'शोध और तर्क देखें',
  },
  id: {
    assumptionsAndLimits: 'Asumsi, batasan, dan validasi',
    createdFor: 'Dibuat untuk {title}',
    evidenceAndMethods: 'Bukti dan metode',
    generationDetails: 'Detail pembuatan',
    hideFullDocument: 'Sembunyikan dokumen lengkap',
    linkedSectionMayHaveMoved: 'Bagian tertaut mungkin telah dipindahkan',
    moreImages: 'Gambar lainnya: {count}',
    openDocument: 'Buka dokumen',
    relatedOutputs: 'Hasil terkait',
    researchAsOf: 'Riset diperbarui hingga {date}',
    showFullDocument: 'Tampilkan dokumen lengkap',
    sources: 'Sumber',
    statusComplete: 'Selesai',
    statusInProgress: 'Sedang berlangsung',
    statusPartial: 'Sebagian',
    statusSuperseded: 'Digantikan',
    viewContextAndRationale: 'Lihat konteks dan alasan',
    viewImage: 'Lihat gambar',
    viewRationale: 'Lihat alasan',
    viewResearchAndRationale: 'Lihat riset dan alasan',
  },
  it: {
    assumptionsAndLimits: 'Ipotesi, limiti e validazione',
    createdFor: 'Creato per {title}',
    evidenceAndMethods: 'Evidenze e metodi',
    generationDetails: 'Dettagli di generazione',
    hideFullDocument: 'Nascondi documento completo',
    linkedSectionMayHaveMoved:
      'La sezione collegata potrebbe essere stata spostata',
    moreImages: 'Altre immagini: {count}',
    openDocument: 'Apri documento',
    relatedOutputs: 'Risultati collegati',
    researchAsOf: 'Ricerca aggiornata al {date}',
    showFullDocument: 'Mostra documento completo',
    sources: 'Fonti',
    statusComplete: 'Completo',
    statusInProgress: 'In corso',
    statusPartial: 'Parziale',
    statusSuperseded: 'Superato',
    viewContextAndRationale: 'Vedi contesto e motivazione',
    viewImage: 'Vedi immagine',
    viewRationale: 'Vedi motivazione',
    viewResearchAndRationale: 'Vedi ricerca e motivazione',
  },
  ja: {
    assumptionsAndLimits: '前提、制約、検証',
    createdFor: '{title} のために作成',
    evidenceAndMethods: '根拠と方法',
    generationDetails: '生成の詳細',
    hideFullDocument: '全文を閉じる',
    linkedSectionMayHaveMoved: 'リンク先のセクションが移動した可能性があります',
    moreImages: 'その他の画像: {count}',
    openDocument: 'ドキュメントを開く',
    relatedOutputs: '関連する出力',
    researchAsOf: '{date} 時点の調査',
    showFullDocument: '全文を表示',
    sources: '情報源',
    statusComplete: '完了',
    statusInProgress: '進行中',
    statusPartial: '一部完了',
    statusSuperseded: '更新済み',
    viewContextAndRationale: '背景と根拠を見る',
    viewImage: '画像を見る',
    viewRationale: '根拠を見る',
    viewResearchAndRationale: '調査と根拠を見る',
  },
  ko: {
    assumptionsAndLimits: '가정, 한계 및 검증',
    createdFor: '{title}용으로 생성',
    evidenceAndMethods: '근거 및 방법',
    generationDetails: '생성 세부정보',
    hideFullDocument: '전체 문서 숨기기',
    linkedSectionMayHaveMoved: '연결된 섹션이 이동되었을 수 있습니다',
    moreImages: '추가 이미지: {count}',
    openDocument: '문서 열기',
    relatedOutputs: '관련 결과물',
    researchAsOf: '{date} 기준 조사',
    showFullDocument: '전체 문서 보기',
    sources: '출처',
    statusComplete: '완료',
    statusInProgress: '진행 중',
    statusPartial: '일부 완료',
    statusSuperseded: '대체됨',
    viewContextAndRationale: '맥락과 근거 보기',
    viewImage: '이미지 보기',
    viewRationale: '근거 보기',
    viewResearchAndRationale: '조사와 근거 보기',
  },
  'pt-BR': {
    assumptionsAndLimits: 'Premissas, limites e validação',
    createdFor: 'Criado para {title}',
    evidenceAndMethods: 'Evidências e métodos',
    generationDetails: 'Detalhes da geração',
    hideFullDocument: 'Ocultar documento completo',
    linkedSectionMayHaveMoved: 'A seção vinculada pode ter sido movida',
    moreImages: 'Mais imagens: {count}',
    openDocument: 'Abrir documento',
    relatedOutputs: 'Resultados relacionados',
    researchAsOf: 'Pesquisa atualizada até {date}',
    showFullDocument: 'Mostrar documento completo',
    sources: 'Fontes',
    statusComplete: 'Concluído',
    statusInProgress: 'Em andamento',
    statusPartial: 'Parcial',
    statusSuperseded: 'Substituído',
    viewContextAndRationale: 'Ver contexto e justificativa',
    viewImage: 'Ver imagem',
    viewRationale: 'Ver justificativa',
    viewResearchAndRationale: 'Ver pesquisa e justificativa',
  },
  ru: {
    assumptionsAndLimits: 'Допущения, ограничения и проверка',
    createdFor: 'Создано для «{title}»',
    evidenceAndMethods: 'Доказательства и методы',
    generationDetails: 'Параметры генерации',
    hideFullDocument: 'Скрыть полный документ',
    linkedSectionMayHaveMoved: 'Связанный раздел, возможно, был перемещён',
    moreImages: 'Другие изображения: {count}',
    openDocument: 'Открыть документ',
    relatedOutputs: 'Связанные результаты',
    researchAsOf: 'Исследование актуально на {date}',
    showFullDocument: 'Показать полный документ',
    sources: 'Источники',
    statusComplete: 'Завершено',
    statusInProgress: 'В работе',
    statusPartial: 'Частично',
    statusSuperseded: 'Заменено',
    viewContextAndRationale: 'Открыть контекст и обоснование',
    viewImage: 'Открыть изображение',
    viewRationale: 'Открыть обоснование',
    viewResearchAndRationale: 'Открыть исследование и обоснование',
  },
  vi: {
    assumptionsAndLimits: 'Giả định, giới hạn và xác thực',
    createdFor: 'Được tạo cho {title}',
    evidenceAndMethods: 'Bằng chứng và phương pháp',
    generationDetails: 'Chi tiết tạo sinh',
    hideFullDocument: 'Ẩn toàn bộ tài liệu',
    linkedSectionMayHaveMoved: 'Mục được liên kết có thể đã được di chuyển',
    moreImages: 'Hình ảnh khác: {count}',
    openDocument: 'Mở tài liệu',
    relatedOutputs: 'Kết quả liên quan',
    researchAsOf: 'Nghiên cứu cập nhật đến {date}',
    showFullDocument: 'Hiện toàn bộ tài liệu',
    sources: 'Nguồn',
    statusComplete: 'Hoàn tất',
    statusInProgress: 'Đang thực hiện',
    statusPartial: 'Một phần',
    statusSuperseded: 'Đã thay thế',
    viewContextAndRationale: 'Xem bối cảnh và lý do',
    viewImage: 'Xem hình ảnh',
    viewRationale: 'Xem lý do',
    viewResearchAndRationale: 'Xem nghiên cứu và lý do',
  },
  zh: {
    assumptionsAndLimits: '假设、限制与验证',
    createdFor: '为“{title}”创建',
    evidenceAndMethods: '证据与方法',
    generationDetails: '生成详情',
    hideFullDocument: '收起完整文档',
    linkedSectionMayHaveMoved: '链接的章节可能已移动',
    moreImages: '更多图片：{count}',
    openDocument: '打开文档',
    relatedOutputs: '相关输出',
    researchAsOf: '研究更新至 {date}',
    showFullDocument: '显示完整文档',
    sources: '来源',
    statusComplete: '已完成',
    statusInProgress: '进行中',
    statusPartial: '部分完成',
    statusSuperseded: '已取代',
    viewContextAndRationale: '查看背景与理由',
    viewImage: '查看图片',
    viewRationale: '查看理由',
    viewResearchAndRationale: '查看研究与理由',
  },
  'zh-TW': {
    assumptionsAndLimits: '假設、限制與驗證',
    createdFor: '為「{title}」建立',
    evidenceAndMethods: '證據與方法',
    generationDetails: '生成詳情',
    hideFullDocument: '收合完整文件',
    linkedSectionMayHaveMoved: '連結的章節可能已移動',
    moreImages: '更多圖片：{count}',
    openDocument: '開啟文件',
    relatedOutputs: '相關輸出',
    researchAsOf: '研究更新至 {date}',
    showFullDocument: '顯示完整文件',
    sources: '來源',
    statusComplete: '已完成',
    statusInProgress: '進行中',
    statusPartial: '部分完成',
    statusSuperseded: '已取代',
    viewContextAndRationale: '查看背景與理由',
    viewImage: '查看圖片',
    viewRationale: '查看理由',
    viewResearchAndRationale: '查看研究與理由',
  },
};

export function getGroundedWorkUiCopy(
  locale: string | null | undefined
): GroundedWorkUiCopy {
  return copy[normalizeLocale(locale)];
}
